import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { notificationsService } from '../services/notificationsService';

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  appointmentId?: number | string;
  userId?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  /** Reîmprospătează numărul de necitite din API (folosit pentru badge la clienți) */
  refreshUnreadCountFromApi: () => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [apiUnreadCount, setApiUnreadCount] = useState<number>(0);

  const hasSessionToken = useCallback(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return !!localStorage.getItem('token');
  }, []);

  const loadNotificationsFromApi = useCallback(async () => {
    if (!hasSessionToken()) {
      setNotifications([]);
      setApiUnreadCount(0);
      return;
    }

    try {
      const response = await notificationsService.getNotifications({ page: 1, limit: 20 });
      const mapped = (response.notifications || []).map((n: any) => ({
        id: n.id,
        type: n.type || 'general',
        title: n.title || '',
        message: n.message || '',
        timestamp: new Date(n.created_at || n.createdAt || n.updated_at || Date.now()),
        read: !!n.read,
        appointmentId: n?.data?.appointmentId,
        userId: n.recipient_id || n.userId,
        priority: (n.priority || 'medium') as Notification['priority'],
      }));
      setNotifications(mapped);
      const stats = await notificationsService.getStats();
      const unread = Number(stats?.unread ?? 0);
      setApiUnreadCount(Number.isNaN(unread) ? mapped.filter((x) => !x.read).length : unread);
    } catch {
      setNotifications([]);
      setApiUnreadCount(0);
    }
  }, [hasSessionToken]);

  useEffect(() => {
    if (!hasSessionToken()) {
      setNotifications([]);
      setApiUnreadCount(0);
      return;
    }

    loadNotificationsFromApi();
    const interval = setInterval(loadNotificationsFromApi, 30000);
    return () => clearInterval(interval);
  }, [hasSessionToken, loadNotificationsFromApi]);

  const unreadCount = apiUnreadCount;

  const refreshUnreadCountFromApi = useCallback(async () => {
    if (!hasSessionToken()) {
      setApiUnreadCount(0);
      return;
    }

    try {
      const stats = await notificationsService.getStats();
      const unread = Number(stats?.unread ?? 0);
      setApiUnreadCount(Number.isNaN(unread) ? 0 : unread);
    } catch {
      setApiUnreadCount(notifications.filter(n => !n.read).length);
    }
  }, [hasSessionToken, notifications]);

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false
    };
    
    setNotifications(prev => [newNotification, ...prev]);
  };

  const markAsRead = (id: string) => {
    notificationsService.markAsRead(id).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setApiUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    notificationsService.markAllAsRead().catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setApiUnreadCount(0);
  };

  const removeNotification = (id: string) => {
    notificationsService.delete(id).catch(() => {});
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      refreshUnreadCountFromApi,
      addNotification,
      markAsRead,
      markAllAsRead,
      removeNotification,
      clearAll
    }}>
      {children}
    </NotificationContext.Provider>
  );
}; 
